import { ref, computed, watch } from 'vue'
import { apiClient } from '../services/apiClient'
import type { BibleVersion, BibleBookOption, BibleReadingSelection, BibleVerse } from '../types/bible'

export function useBibleReading() {
  const versions = ref<BibleVersion[]>([])
  const books = ref<BibleBookOption[]>([])
  const chapters = ref<number[]>([])

  const selection = ref<BibleReadingSelection>({
    versionCode: null,
    bookId: null,
    chapter: null,
  })

  const loading = ref(false)
  const error = ref<string | null>(null)
  const verses = ref<BibleVerse[]>([])
  const includeStrong = ref(false)
  let verseAbortController: AbortController | null = null
  // Suppresses the selection watcher while selectVersion handles its own reload
  let isSwitchingVersion = false

  const loadVersions = async () => {
    try {
      loading.value = true
      error.value = null
      const data = await apiClient.getVersions()
      versions.value = data
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load versions'
    } finally {
      loading.value = false
    }
  }

  const selectVersion = async (code: string) => {
    const previousBookId = selection.value.bookId
    const previousChapter = selection.value.chapter

    isSwitchingVersion = true

    selection.value = {
      versionCode: code,
      bookId: previousBookId,
      chapter: previousChapter,
    }
    books.value = []
    chapters.value = []

    try {
      await loadBooks()

      // Restore chapter list and re-fetch verses when a book was already selected
      if (previousBookId && books.value.length > 0) {
        const book = books.value.find(b => b.id === previousBookId)
        if (book) {
          chapters.value = Array.from({ length: book.chapterCount }, (_, i) => i + 1)
          if (previousChapter) {
            await loadChapterVerses()
          }
        }
      }
    } finally {
      isSwitchingVersion = false
    }
  }

  const loadBooks = async () => {
    if (!selection.value.versionCode) return

    try {
      loading.value = true
      error.value = null

      const data = await apiClient.getBooks(selection.value.versionCode)
      books.value = data.map((b: any) => ({
        id: b.id,
        chiShort: b.chi_short,
        chiFull: b.chi_full,
        engShort: b.eng_short,
        chapterCount: b.chapterCount || 1,
      }))
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load books'
    } finally {
      loading.value = false
    }
  }

  const selectBook = (id: number) => {
    selection.value = {
      ...selection.value,
      bookId: id,
      chapter: null,
    }
    loadChapters()
  }

  const loadChapters = () => {
    try {
      if (!selection.value.bookId) return

      const book = books.value.find(b => b.id === selection.value.bookId)
      if (!book) return

      const chapterCount = book.chapterCount
      chapters.value = Array.from({ length: chapterCount }, (_, i) => i + 1)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load chapters'
    }
  }

  const selectChapter = (n: number) => {
    selection.value = {
      ...selection.value,
      chapter: n,
    }
  }

  const loadChapterVerses = async () => {
    if (!selection.value.versionCode || !selection.value.bookId || !selection.value.chapter) {
      verses.value = []
      return
    }

    verseAbortController?.abort()
    verseAbortController = new AbortController()

    try {
      loading.value = true
      error.value = null

      const book = books.value.find(b => b.id === selection.value.bookId)
      if (!book) {
        verses.value = []
        return
      }

      const chapterData = await apiClient.getChapter(book.id.toString(), selection.value.chapter, selection.value.versionCode || undefined, includeStrong.value)
      if (verseAbortController.signal.aborted) return

      if (chapterData && typeof chapterData.content === 'string') {
        try {
          const parsed = JSON.parse(chapterData.content)
          const records = Array.isArray(parsed.record) ? parsed.record : []
          // 過濾出正確章節的經文
          const chapterRecords = records.filter((r: any) => r.chap === selection.value.chapter)
          verses.value = chapterRecords.map((v: any) => ({
            verse: v.sec,
            text: v.bible_text,
          }))
        } catch (e) {
          verses.value = []
        }
      } else {
        verses.value = []
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      error.value = err instanceof Error ? err.message : 'Failed to load verses'
      verses.value = []
    } finally {
      loading.value = false
    }
  }

  // 自動監測選擇變化，加載對應章節的經文
  // Version switches are handled by selectVersion directly; skip watcher during that flow.
  watch(
    () => [selection.value.versionCode, selection.value.bookId, selection.value.chapter],
    () => {
      if (!isSwitchingVersion) {
        loadChapterVerses()
      }
    }
  )

  const isSelectionComplete = computed(() => {
    return (
      selection.value.versionCode &&
      selection.value.bookId &&
      selection.value.chapter !== null
    )
  })

  const isBookSelectDisabled = computed(() => {
    return !selection.value.versionCode || books.value.length === 0
  })

  const isChapterSelectDisabled = computed(() => {
    return !selection.value.bookId || chapters.value.length === 0
  })

  const setIncludeStrong = (value: boolean) => {
    includeStrong.value = value
    loadChapterVerses()
  }

  return {
    versions,
    books,
    chapters,
    selection,
    loading,
    error,
    verses,
    includeStrong,
    loadVersions,
    selectVersion,
    loadBooks,
    selectBook,
    selectChapter,
    loadChapterVerses,
    setIncludeStrong,
    isSelectionComplete,
    isBookSelectDisabled,
    isChapterSelectDisabled,
  }
}
